import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, computed, signal } from '@angular/core'
import { Observable, catchError, finalize, firstValueFrom, map, of, switchMap, tap, throwError } from 'rxjs'
import { environment } from '@env/environment'
import { Authenticate } from '../models/auth.model'
import { User, UserRole, UserStatus } from '../models/user.model'

export const SESSION_USER_KEY = '__session_user'
export const SESSION_PROFILE_KEY = '__session_profile'

/** Renova antes de expirar, como no portal-frontend. */
const EXPIRATION_MARGIN_MS = 4 * 60_000

interface Session extends Authenticate {
    expiresAtMs: number
}

export interface SigninInput {
    username: string
    password: string
    newPassword?: string
}

/**
 * Sessão do participante no authentication-backend (v2). O access token fica só em memória; o
 * refresh usa o cookie HttpOnly do próprio serviço de autenticação, por isso todas as chamadas de
 * sessão vão com `withCredentials`.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly url = environment.urlAuth
    private readonly session = signal<Session | null>(null)

    readonly user = signal<User | null>(readStoredProfile())
    readonly authenticated = computed(() => this.session() !== null && this.user() !== null)
    /** Só o papel de cidadão (spec 048): sem fornecedor nem assinante, a área dele é a do visitante. */
    readonly isVisitorOnly = computed(() => {
        const roles = this.user()?.roles ?? []

        return roles.includes(UserRole.VISITOR) && !roles.includes(UserRole.SUPPLIER) && !roles.includes(UserRole.SIGNER)
    })

    constructor(private readonly http: HttpClient) {}

    /** APP_INITIALIZER: recupera a sessão de quem já entrou antes; nunca bloqueia o boot. */
    bootstrapSession(): Promise<void> {
        if (!localStorage.getItem(SESSION_USER_KEY)) {
            this.clearSession()
            return Promise.resolve()
        }

        return firstValueFrom(
            this.refresh().pipe(
                map(() => undefined),
                catchError(() => {
                    this.clearSession()
                    return of(undefined)
                })
            )
        )
    }

    /**
     * Quando o status for FORCE_CHANGE_PASSWORD a sessão não é aberta: o chamador pede a nova senha
     * e repete o signin com `newPassword`.
     */
    signin(input: SigninInput): Observable<Authenticate> {
        return this.http.post<Authenticate>(`${this.url}/v2/signin`, input, { withCredentials: true }).pipe(
            switchMap(response => {
                const authenticate: Authenticate = { ...response, username: response.username ?? input.username }

                if (!authenticate.token || authenticate.status === UserStatus.FORCE_CHANGE_PASSWORD) {
                    return of(authenticate)
                }

                this.setSession(authenticate)

                return this.rememberDevice(authenticate).pipe(
                    switchMap(() => this.loadProfile(authenticate.token)),
                    map(() => authenticate)
                )
            })
        )
    }

    refresh(): Observable<Authenticate> {
        const username = this.session()?.username ?? localStorage.getItem(SESSION_USER_KEY)

        if (!username) {
            return throwError(() => new Error('Sessão não encontrada'))
        }

        return this.http.post<Authenticate>(`${this.url}/v2/refresh`, { username }, { withCredentials: true }).pipe(
            map(response => {
                const authenticate: Authenticate = { ...response, username }
                this.setSession(authenticate)
                return authenticate
            })
        )
    }

    /** Sessão válida para uma chamada autenticada, renovando se for preciso. */
    ensureSession(): Observable<Authenticate | null> {
        const current = this.session()

        if (current && !isExpired(current)) {
            return of(current)
        }

        if (!localStorage.getItem(SESSION_USER_KEY)) {
            return of(null)
        }

        return this.refresh().pipe(catchError(() => {
            this.clearSession()
            return of(null)
        }))
    }

    logout(): Observable<void> {
        return this.http.post(`${this.url}/v2/logout`, {}, { withCredentials: true }).pipe(
            map(() => undefined),
            catchError(() => of(undefined)),
            finalize(() => this.clearSession())
        )
    }

    /**
     * Encaminha para a área do participante (app.silgov.com.br), já autenticado. A sessão vai no fragmento
     * (`#session=`), que não chega ao servidor nem aos logs do CloudFront e do S3, e o app a tira da barra assim que
     * a lê. O `target` (caminho relativo, sem dado pessoal) vai em `?redirect=`, que o app limita à própria origem.
     */
    redirectToParticipantArea(target?: string): void {
        window.open(this.participantAreaUrl(target), '_self')
    }

    participantAreaUrl(target?: string): string {
        const session = this.session()
        const query = target && target.startsWith('/') && !target.startsWith('//') ? `?redirect=${encodeURIComponent(target)}` : ''
        const fragment = session ? `#session=${encodeURIComponent(btoa(JSON.stringify(stripSession(session))))}` : ''

        return `${environment.urlParticipant}/auth/signin${query}${fragment}`
    }

    get forgotPasswordUrl(): string {
        return `${environment.urlParticipant}/auth/forgot-password`
    }

    get token(): string | null {
        return this.session()?.token ?? null
    }

    private loadProfile(token: string): Observable<User> {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`)

        return this.http.get<User>(`${this.url}/users/profile`, { headers }).pipe(
            tap(user => {
                localStorage.setItem(SESSION_PROFILE_KEY, JSON.stringify(user))
                this.user.set(user)
            })
        )
    }

    /** Device tracking do Cognito: só quando o signin devolve as chaves do dispositivo. */
    private rememberDevice(session: Authenticate): Observable<unknown> {
        if (!session.deviceKey || !session.deviceGroupKey) {
            return of(null)
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${session.token}`)
        const options = { headers, withCredentials: true }

        return this.http
            .post<{ deviceKey: string }>(`${this.url}/devices/confirm`, {
                deviceKey: session.deviceKey,
                deviceGroupKey: session.deviceGroupKey
            }, options)
            .pipe(
                switchMap(confirm => this.http.post(`${this.url}/devices/remember`, { deviceKey: confirm.deviceKey }, options)),
                // Falha no device tracking não impede o login.
                catchError(() => of(null))
            )
    }

    private setSession(authenticate: Authenticate): void {
        this.session.set({ ...authenticate, expiresAtMs: expiresAt(authenticate) })

        if (authenticate.username) {
            localStorage.setItem(SESSION_USER_KEY, authenticate.username)
        }
    }

    private clearSession(): void {
        this.session.set(null)
        this.user.set(null)
        localStorage.removeItem(SESSION_USER_KEY)
        localStorage.removeItem(SESSION_PROFILE_KEY)
    }
}

function isExpired(session: Session): boolean {
    return Date.now() >= session.expiresAtMs - EXPIRATION_MARGIN_MS
}

/** `expiresIn` (segundos) é preferido; `expireAt` vem no horário de Fortaleza (UTC−3) sem fuso. */
function expiresAt(authenticate: Authenticate): number {
    if (authenticate.expiresIn) {
        return Date.now() + authenticate.expiresIn * 1000
    }

    if (authenticate.expireAt) {
        const parsed = Date.parse(`${authenticate.expireAt.slice(0, 19)}-03:00`)

        if (!Number.isNaN(parsed)) {
            return parsed
        }
    }

    return 0
}

function stripSession(session: Session): Authenticate {
    const { expiresAtMs: _ignored, ...authenticate } = session
    return authenticate
}

function readStoredProfile(): User | null {
    try {
        const stored = localStorage.getItem(SESSION_PROFILE_KEY)
        return stored ? JSON.parse(stored) as User : null
    } catch {
        return null
    }
}
